import axios from "axios";
import reduxStore from "../utils/redux/store";
import constants from "../configs/constants";
import secureAxios from "./axios/secureAxios";

// Axios instance for uro-mind custom routes (threads/create, threads, chat/*)
const mindAxios = axios.create({
  baseURL: constants.MIND_URL,
  headers: { "Content-Type": "application/json" },
});

const attachAuthInterceptor = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = reduxStore.store.getState().auth.token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error.response?.data || error)
  );
};

attachAuthInterceptor(mindAxios);

// ==================== THREADS ====================

async function createThread({ brand_id, brand_name, title }) {
  // POST /threads/create — auth extracts clientId from JWT; brand metadata stored in thread
  const { data } = await mindAxios.post("/threads/create", {
    brandId: brand_id,
    brandName: brand_name,
    title: title || `Chat with ${brand_name || 'Brand'}`,
  });
  // Custom route returns { success: true, data: thread }
  return data;
}

async function listThreads({ brandId } = {}) {
  // GET /threads — returns threads for authenticated user, optionally filtered by brandId
  const params = brandId ? { brandId } : {};
  const { data } = await mindAxios.get("/threads", { params });
  // Custom route returns { success: true, data: [...] }
  return data;
}

async function getThread(threadId) {
  // GET /threads/:threadId/messages — custom auth-protected route that returns thread + messages
  const { data } = await mindAxios.get(`/threads/${threadId}/messages`);
  return data;
}

async function deleteThread(threadId) {
  // DELETE /threads/:threadId — custom route with ownership check
  const { data } = await mindAxios.delete(`/threads/${threadId}`);
  return data;
}

async function getThreadDebug(threadId) {
  // GET /threads/:threadId/debug — raw PG timing, token estimates, Langfuse traces
  const { data } = await mindAxios.get(`/threads/${threadId}/debug`);
  return data;
}

async function getThreadAnalysis(threadId) {
  // GET /threads/:threadId/analysis — background auto-analysis results
  const { data } = await mindAxios.get(`/threads/${threadId}/analysis`);
  return data;
}

// ==================== IMAGE JOBS ====================

async function getImageJob(jobId) {
  const { data } = await secureAxios.get(`/image-jobs/image-job/${jobId}`);
  return data;
}

async function listCompletedImageJobs({ limit = 100 } = {}) {
  const { data } = await secureAxios.get("/image-jobs", {
    params: { status: "completed", limit },
  });
  return data;
}

async function listBrandImageJobs({ brandId, threadId, limit = 100 } = {}) {
  if (threadId) {
    // Conversation mode: jobs for a specific thread
    const { data } = await secureAxios.get("/image-jobs", {
      params: { status: "completed", limit, agent_thread_id: threadId },
    });
    return data;
  }

  if (brandId) {
    // Brand mode: fetch all threads for this brand, then their image jobs.
    // We use thread-based filtering because brand_id on image_job may be null
    // for jobs created before that column was backfilled.
    const threadsRes = await mindAxios.get("/threads", { params: { brandId } });
    const threads = threadsRes?.data?.data || [];

    if (threads.length === 0) {
      return { data: { items: [], pagination: { total: 0, page: 1, limit, totalPages: 0 } } };
    }

    const threadIds = threads.map((t) => t.id);
    const { data } = await secureAxios.get("/image-jobs", {
      params: { status: "completed", limit, agent_thread_id: threadIds },
    });
    return data;
  }

  return null;
}

// ==================== LLM MODELS ====================

async function listLLMModels() {
  const { data } = await mindAxios.get('/chat/models');
  return data;
}

// ==================== HELPERS ====================

/** Get auth token for SSE streaming (useChat needs it) */
function getAuthToken() {
  return reduxStore.store.getState().auth.token;
}

/** Get the streaming endpoint URL */
function getStreamUrl() {
  return `${constants.MIND_URL}/chat/stream`;
}

/** Get uro-api base URL */
function getApiUrl() {
  return constants.API_URL;
}

export const agentActions = {
  createThread,
  listThreads,
  getThread,
  deleteThread,
  getThreadDebug,
  getThreadAnalysis,
  getImageJob,
  listCompletedImageJobs,
  listBrandImageJobs,
  listLLMModels,
  getAuthToken,
  getStreamUrl,
  getApiUrl,
};

export default agentActions;
